import Button from '@/components/Button';
import Footer from '@/components/Footer';
import Navbar from '@/components/Navbar';
import { animationConfig, getPageAnimation } from '@/config/animations';
import { motion } from 'framer-motion';
import { Mail, Send, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const ContactPage = () => {
  const AnimationWrapper = animationConfig.enablePageTransitions ? motion.div : 'div';

  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');

  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  const [waitlistError, setWaitlistError] = useState('');

  const apiUrl = import.meta.env.VITE_API_URL;

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactSubmitting(true);
    setContactError('');
    setContactSuccess(false);

    try {
      const response = await fetch(`${apiUrl}api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setContactSuccess(true);
        setContactForm({ name: '', email: '', message: '' });
      } else {
        setContactError(data.message || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      setContactError('Failed to send message. Please try again.');
    } finally {
      setContactSubmitting(false);
    }
  };

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setWaitlistSubmitting(true);
    setWaitlistError('');
    setWaitlistSuccess(false);

    try {
      const response = await fetch(`${apiUrl}api/waitlist/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: waitlistEmail }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setWaitlistSuccess(true);
        setWaitlistEmail('');
      } else {
        setWaitlistError(data.message || 'Failed to join waitlist. Please try again.');
      }
    } catch (error) {
      setWaitlistError('Failed to join waitlist. Please try again.');
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  return (
    <AnimationWrapper
      {...getPageAnimation()}
      className="min-h-screen bg-gradient-to-b from-dark-950 via-dark-900 to-dark-950"
    >
      <Navbar />

      <section className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Get in <span className="text-gradient">Touch</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Have questions about our development services or interested in working together?
              We'd love to hear from you.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="glass-effect p-8 rounded-2xl">
                <Mail className="w-10 h-10 text-primary-400 mb-6" />
                <h2 className="text-2xl font-bold text-white mb-4">
                  Send us a Message
                </h2>
                <p className="text-gray-400 mb-6">
                  Fill out the form below and we'll get back to you within 24 hours.
                </p>
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div>
                    <label className="block text-gray-300 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg bg-dark-800 border border-dark-700 text-white focus:border-primary-500 focus:outline-none transition-colors"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg bg-dark-800 border border-dark-700 text-white focus:border-primary-500 focus:outline-none transition-colors"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2">
                      Message *
                    </label>
                    <textarea
                      required
                      rows={6}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg bg-dark-800 border border-dark-700 text-white focus:border-primary-500 focus:outline-none transition-colors resize-none"
                      placeholder="Tell us about your project or question..."
                    />
                  </div>

                  {contactSuccess && (
                    <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 mb-4">
                      Message sent successfully! We'll get back to you soon.
                    </div>
                  )}
                  {contactError && (
                    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 mb-4">
                      {contactError}
                    </div>
                  )}
                  <Button type="submit" size="lg" className="w-full group" disabled={contactSubmitting}>
                    {contactSubmitting ? 'Sending...' : 'Send Message'}
                    {!contactSubmitting && <Send className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />}
                  </Button>
                </form>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className="glass-effect p-8 rounded-2xl">
                <div className="inline-flex p-3 rounded-lg bg-gradient-to-br from-primary-400 to-primary-600 mb-6">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-3">
                  Email Us Directly
                </h3>
                <p className="text-gray-400 mb-4">
                  Prefer email? Send us a message directly at:
                </p>
                <a
                  href="mailto:contact@nirolabs.ai"
                  className="text-primary-400 text-lg font-medium hover:text-primary-300 transition-colors"
                >
                  contact@nirolabs.ai
                </a>
                <p className="text-gray-500 text-sm mt-4">
                  We typically respond within 24 hours
                </p>
              </div>

              <div className="glass-effect p-8 rounded-2xl">
                <Sparkles className="w-10 h-10 text-primary-400 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-3">
                  Join VisualForge Waitlist
                </h3>
                <p className="text-gray-400 mb-6">
                  Be the first to know when VisualForge launches. Get early access
                  and exclusive updates.
                </p>
                <form onSubmit={handleWaitlistSubmit} className="space-y-4">
                  <input
                    type="email"
                    required
                    value={waitlistEmail}
                    onChange={(e) => setWaitlistEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 rounded-lg bg-dark-800 border border-dark-700 text-white focus:border-primary-500 focus:outline-none transition-colors"
                    disabled={waitlistSubmitting}
                  />
                  {waitlistSuccess && (
                    <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm">
                      Welcome to the waitlist! Check your email for confirmation.
                    </div>
                  )}
                  {waitlistError && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                      {waitlistError}
                    </div>
                  )}
                  <Button type="submit" size="lg" className="w-full" disabled={waitlistSubmitting}>
                    {waitlistSubmitting ? 'Joining...' : 'Join Waitlist'}
                  </Button>
                </form>
              </div>

              <div className="glass-effect p-8 rounded-2xl">
                <h3 className="text-xl font-semibold text-white mb-3">
                  Looking for Development Services?
                </h3>
                <p className="text-gray-400 mb-4">
                  Let's discuss how we can help bring your project to life.
                </p>
                <Link to="/services">
                  <Button variant="outline" size="sm">
                    Learn About Services
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </AnimationWrapper>
  );
};

export default ContactPage;